import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useAuth } from '../contexts/AuthContext';
import { useRepoContext } from '../contexts/RepoContext';
import { FolderPickerModal } from './FolderPickerModal';

type AccessType = 'docs-only' | 'docs-and-code';

interface RepoGrant {
    repoId: string;
    overrides?: {
        includePaths?: string[];
        excludePaths?: string[];
    };
}

interface Role {
    id: string;
    name: string;
    description?: string;
    accessType: AccessType;
    repoGrants: RepoGrant[];
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
}

interface RoleDraft {
    name: string;
    description: string;
    accessType: AccessType;
    repoGrants: RepoGrant[];
}

const EMPTY_DRAFT: RoleDraft = { name: '', description: '', accessType: 'docs-only', repoGrants: [] };

export function AdminRolesPage() {
    const { tokenReady } = useAuth();
    const { allowedRepos } = useRepoContext();
    const [roles, setRoles] = useState<Role[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editing, setEditing] = useState<Role | 'new' | null>(null);
    const [draft, setDraft] = useState<RoleDraft>(EMPTY_DRAFT);
    const [saving, setSaving] = useState(false);
    const [pickerRepoId, setPickerRepoId] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await apiCall<Role[]>('/manage/roles');
            setRoles(r);
            setError(null);
        } catch (err) {
            setRoles([]);
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        }
    }, []);
    useEffect(() => { if (tokenReady) void load(); }, [tokenReady, load]);

    function startNew() {
        setDraft({ ...EMPTY_DRAFT });
        setEditing('new');
    }
    function startEdit(role: Role) {
        setDraft({
            name: role.name,
            description: role.description ?? '',
            accessType: role.accessType,
            repoGrants: role.repoGrants.map((g) => ({
                repoId: g.repoId,
                overrides: g.overrides ? { ...g.overrides } : undefined
            }))
        });
        setEditing(role);
    }
    function cancel() {
        setEditing(null);
        setDraft(EMPTY_DRAFT);
    }

    async function save() {
        if (!draft.name.trim()) {
            setError('Name is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const body = {
                name: draft.name.trim(),
                description: draft.description.trim() || undefined,
                accessType: draft.accessType,
                repoGrants: draft.repoGrants
            };
            if (editing === 'new') {
                await apiCall<Role>('/manage/roles', { method: 'POST', body });
            } else if (editing) {
                await apiCall<Role>(`/manage/roles/${encodeURIComponent(editing.id)}`, { method: 'PATCH', body });
            }
            setEditing(null);
            setDraft(EMPTY_DRAFT);
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    async function del(role: Role) {
        if (!window.confirm(`Delete role "${role.name}"? Users assigned this role will lose any access it granted.`)) return;
        setSaving(true);
        setError(null);
        try {
            await apiCall(`/manage/roles/${encodeURIComponent(role.id)}`, { method: 'DELETE' });
            await load();
        } catch (err) {
            setError(err instanceof ApiError ? err.message : (err as Error).message);
        } finally {
            setSaving(false);
        }
    }

    function addRepoGrant() {
        const candidates = (allowedRepos ?? []).map((r) => r.id).filter((id) => !draft.repoGrants.some((g) => g.repoId === id));
        if (candidates.length === 0) return;
        setDraft((d) => ({ ...d, repoGrants: [...d.repoGrants, { repoId: candidates[0] }] }));
    }
    function removeRepoGrant(repoId: string) {
        setDraft((d) => ({ ...d, repoGrants: d.repoGrants.filter((g) => g.repoId !== repoId) }));
    }
    function setRepoGrantRepoId(oldId: string, newId: string) {
        setDraft((d) => ({
            ...d,
            repoGrants: d.repoGrants.map((g) => (g.repoId === oldId ? { ...g, repoId: newId } : g))
        }));
    }
    function setOverrides(repoId: string, includes: string[], excludes: string[]) {
        setDraft((d) => ({
            ...d,
            repoGrants: d.repoGrants.map((g) => {
                if (g.repoId !== repoId) return g;
                const overrides: { includePaths?: string[]; excludePaths?: string[] } = {};
                if (includes.length > 0) overrides.includePaths = includes;
                if (excludes.length > 0) overrides.excludePaths = excludes;
                return {
                    ...g,
                    overrides: overrides.includePaths || overrides.excludePaths ? overrides : undefined
                };
            })
        }));
    }
    function removePath(repoId: string, kind: 'include' | 'exclude', path: string) {
        setDraft((d) => ({
            ...d,
            repoGrants: d.repoGrants.map((g) => {
                if (g.repoId !== repoId) return g;
                const key = kind === 'include' ? 'includePaths' : 'excludePaths';
                const next = (g.overrides?.[key] ?? []).filter((p) => p !== path);
                const overrides = { ...(g.overrides ?? {}) };
                if (next.length === 0) delete overrides[key];
                else overrides[key] = next;
                return {
                    ...g,
                    overrides: overrides.includePaths || overrides.excludePaths ? overrides : undefined
                };
            })
        }));
    }

    const reposById = useMemo(() => {
        const m = new Map<string, string>();
        for (const r of allowedRepos ?? []) m.set(r.id, r.id);
        return m;
    }, [allowedRepos]);

    return (
        <div className="admin-page">
            <header className="admin-page__header">
                <div>
                    <h1>Roles</h1>
                    <p className="admin-page__sub">
                        Bundle repo access by job function. Assign a role to a user instead of granting per-repo every
                        time. Per-user grants in the Users tab act as overrides on top.
                    </p>
                </div>
                {!editing && (
                    <button className="btn btn--primary" onClick={startNew}>+ New role</button>
                )}
            </header>

            {error && <div className="admin-page__error">{error}</div>}

            {editing && (
                <section className="role-editor">
                    <h2>{editing === 'new' ? 'New role' : `Edit role: ${editing.name}`}</h2>
                    <label className="form-group">
                        <span className="form-label">Name</span>
                        <input
                            type="text"
                            value={draft.name}
                            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                            placeholder="e.g. HR Reviewer"
                            disabled={saving}
                            autoFocus
                        />
                    </label>
                    <label className="form-group">
                        <span className="form-label">Description (optional)</span>
                        <input
                            type="text"
                            value={draft.description}
                            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                            placeholder="What this role is for"
                            disabled={saving}
                        />
                    </label>
                    <fieldset className="form-group form-group--fieldset">
                        <legend className="form-label">Access type</legend>
                        <label className="radio-row">
                            <input
                                type="radio"
                                name="access-type"
                                checked={draft.accessType === 'docs-only'}
                                onChange={() => setDraft((d) => ({ ...d, accessType: 'docs-only' }))}
                                disabled={saving}
                            />
                            <span>Docs only — only Markdown files (<code>.md</code>, <code>.mdx</code>)</span>
                        </label>
                        <label className="radio-row">
                            <input
                                type="radio"
                                name="access-type"
                                checked={draft.accessType === 'docs-and-code'}
                                onChange={() => setDraft((d) => ({ ...d, accessType: 'docs-and-code' }))}
                                disabled={saving}
                            />
                            <span>Docs and code — every reviewable file</span>
                        </label>
                    </fieldset>

                    <h3 className="role-editor__section">Repositories granted by this role</h3>
                    <p className="form-hint">
                        Pick which registered repos this role covers. By default each grants the whole repo, filtered
                        by the access type above. Use <strong>overrides</strong> on a repo to scope access to specific
                        folders or files — when you set <strong>Include</strong> paths, those become the entire allowed
                        set for the repo (still filtered by the access type). <strong>Exclude</strong> paths always
                        subtract.
                    </p>
                    {draft.repoGrants.length === 0 && (
                        <div className="admin-page__empty">No repos yet. Add at least one.</div>
                    )}
                    {draft.repoGrants.map((g) => (
                        <div key={g.repoId} className="role-grant">
                            <div className="role-grant__head">
                                <select
                                    value={g.repoId}
                                    onChange={(e) => setRepoGrantRepoId(g.repoId, e.target.value)}
                                    disabled={saving}
                                >
                                    <option value={g.repoId}>{g.repoId}</option>
                                    {(allowedRepos ?? [])
                                        .filter((r) => r.id !== g.repoId && !draft.repoGrants.some((x) => x.repoId === r.id))
                                        .map((r) => (
                                            <option key={r.id} value={r.id}>{r.id}</option>
                                        ))}
                                </select>
                                <button className="btn btn--ghost btn--small" onClick={() => removeRepoGrant(g.repoId)} disabled={saving}>
                                    Remove
                                </button>
                            </div>
                            <div className="role-grant__overrides">
                                <div className="form-group">
                                    <span className="form-label">Path overrides (optional)</span>
                                    <p className="form-hint">
                                        <strong>Include:</strong> when set, the role grants access to ONLY these paths
                                        (still filtered by the access type above — e.g. a docs-only role with
                                        <code> docs/PRD.md</code> included grants exactly that file).
                                        <br />
                                        <strong>Exclude:</strong> always subtracts from whatever is in scope.
                                        <br />
                                        Leave both blank to grant the whole repo (subject to the access type).
                                    </p>
                                    <div className="role-grant__path-chips">
                                        {(g.overrides?.includePaths ?? []).map((p) => (
                                            <span key={`inc-${p}`} className="role-chip role-chip--include">
                                                <span className="role-chip__sign">＋</span>
                                                <code>{p}</code>
                                                <button
                                                    type="button"
                                                    className="role-chip__x"
                                                    onClick={() => removePath(g.repoId, 'include', p)}
                                                    disabled={saving}
                                                    aria-label={`Remove include ${p}`}
                                                >×</button>
                                            </span>
                                        ))}
                                        {(g.overrides?.excludePaths ?? []).map((p) => (
                                            <span key={`exc-${p}`} className="role-chip role-chip--exclude">
                                                <span className="role-chip__sign">−</span>
                                                <code>{p}</code>
                                                <button
                                                    type="button"
                                                    className="role-chip__x"
                                                    onClick={() => removePath(g.repoId, 'exclude', p)}
                                                    disabled={saving}
                                                    aria-label={`Remove exclude ${p}`}
                                                >×</button>
                                            </span>
                                        ))}
                                        {(g.overrides?.includePaths ?? []).length === 0
                                          && (g.overrides?.excludePaths ?? []).length === 0
                                          && <em className="form-hint">No overrides — whole repo (filtered by the access type).</em>}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn--ghost btn--small"
                                        onClick={() => setPickerRepoId(g.repoId)}
                                        disabled={saving}
                                    >
                                        Pick include / exclude paths…
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                    <button className="btn btn--ghost btn--small" onClick={addRepoGrant} disabled={saving}>
                        + Add repo
                    </button>

                    <footer className="role-editor__actions">
                        <button className="btn btn--ghost" onClick={cancel} disabled={saving}>Cancel</button>
                        <button className="btn btn--primary" onClick={() => void save()} disabled={saving || !draft.name.trim()}>
                            {saving ? 'Saving…' : 'Save role'}
                        </button>
                    </footer>
                </section>
            )}

            {pickerRepoId && (() => {
                const grant = draft.repoGrants.find((g) => g.repoId === pickerRepoId);
                if (!grant) return null;
                return (
                    <FolderPickerModal
                        repoId={pickerRepoId}
                        fullName={pickerRepoId}
                        initialIncludes={grant.overrides?.includePaths ?? []}
                        initialExcludes={grant.overrides?.excludePaths ?? []}
                        title={`Path overrides — ${pickerRepoId}`}
                        indexedOnly
                        helperText={
                            <>
                                Click <strong>Include</strong> to scope this role to ONLY those folders/files
                                (still filtered by the role's access type). Click <strong>Exclude</strong> to
                                subtract. Leave everything blank to grant the whole repo. Only paths that are
                                actually indexed in this repo are shown — at the repo level the admin has already
                                chosen what to index.
                            </>
                        }
                        onClose={() => setPickerRepoId(null)}
                        onConfirm={({ includes, excludes }) => {
                            setOverrides(pickerRepoId, includes, excludes);
                            setPickerRepoId(null);
                        }}
                    />
                );
            })()}

            {!editing && (
                <>
                    {roles === null && <div className="admin-page__loading">Loading…</div>}
                    {roles && roles.length === 0 && <div className="admin-page__empty">No roles yet. Create one.</div>}
                    {roles && roles.length > 0 && (
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Access type</th>
                                    <th>Repos</th>
                                    <th>Updated</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {roles.map((r) => (
                                    <Fragment key={r.id}>
                                        <tr>
                                            <td>
                                                <strong>{r.name}</strong>
                                                {r.description && <div className="role-list__desc">{r.description}</div>}
                                            </td>
                                            <td>{r.accessType === 'docs-only' ? 'Docs only' : 'Docs + code'}</td>
                                            <td>
                                                <ul className="grant-list">
                                                    {r.repoGrants.map((g) => (
                                                        <li key={g.repoId}>
                                                            <code>{g.repoId}</code>
                                                            {g.overrides?.includePaths && g.overrides.includePaths.length > 0 && (
                                                                <span className="role-list__ovr"> · +{g.overrides.includePaths.length}</span>
                                                            )}
                                                            {g.overrides?.excludePaths && g.overrides.excludePaths.length > 0 && (
                                                                <span className="role-list__ovr"> · −{g.overrides.excludePaths.length}</span>
                                                            )}
                                                            {!reposById.has(g.repoId) && (
                                                                <span className="role-list__warn"> (repo not active)</span>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </td>
                                            <td>{r.updatedAt ? new Date(r.updatedAt).toLocaleString() : new Date(r.createdAt).toLocaleString()}</td>
                                            <td>
                                                <button className="btn btn--ghost btn--small" onClick={() => startEdit(r)}>Edit</button>
                                                <button className="btn btn--ghost btn--small" onClick={() => void del(r)}>Delete</button>
                                            </td>
                                        </tr>
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </>
            )}
        </div>
    );
}
